import clsx from "clsx";
import type { MouseEvent, Ref } from "react";

export type AnchorNavItem = {
  number: string;
  label: string;
  anchor: string;
  dark: boolean;
};

type Props = {
  items: AnchorNavItem[];
  /**
   * Whether to draw the edge rail. Off when the bar sits in a section group,
   * which owns a rail of its own.
   */
  showRail?: boolean;
  /** Accessible name for the bar. */
  navLabel?: string;
  /** Set by the page builder when the bar is a section of its own. */
  sectionId?: string;
  /** Anchor of the section the page is in, once a script is tracking it. */
  current?: string | null;
  /** The script is running: the rail's marks turn clickable. */
  enhanced?: boolean;
  stuck?: boolean;
  railOverDark?: boolean;
  innerRef?: Ref<HTMLDivElement>;
  /** Takes over the jump; without it the links are plain anchors. */
  onJump?: (anchor: string) => void;
};

/**
 * The port of sections/page-builder/anchor-nav.php: the markup only.
 *
 * The chrome that runs alongside the six commitments: a navy bar of jump links
 * that sticks under the header, and — on wide screens only — a rail of dashes
 * pinned to the left edge, marking the same commitments.
 *
 * The bar is plain anchors, so it jumps without JavaScript; whoever renders it
 * (SectionGroup here) takes over to place the bar, animate the jump clear of the
 * header and mark whichever commitment is in view, and passes the state back in.
 * The rail is a shortcut for the same links, so it is hidden from assistive
 * technology and only becomes clickable once the script is running.
 */
export default function AnchorNav({
  items: allItems,
  showRail = true,
  navLabel,
  sectionId,
  current,
  enhanced,
  stuck,
  railOverDark,
  innerRef,
  onJump,
}: Props) {
  const items = allItems.filter((item) => item.anchor);

  if (!items.length) {
    return null;
  }

  const jump = (event: MouseEvent, anchor: string) => {
    if (!onJump) {
      return;
    }

    event.preventDefault();
    onJump(anchor);
  };

  return (
    <section
      // js-charter-nav is the theme's script hook, kept by name.
      className={clsx(
        "js-charter-nav relative z-4",
        stuck && "h-[var(--nav-height)]",
      )}
      data-id={sectionId || undefined}
      aria-label={navLabel || undefined}
    >
      {/* The ground lives here rather than on the section: once stuck, the section is an empty spacer. */}
      <div
        ref={innerRef}
        className={clsx(
          "w-full border-y border-white/7 bg-brand-blue",
          stuck && "fixed top-[var(--nav-top)] right-0 left-0 z-4",
        )}
      >
        <div className="shell shell-s">
          <ul className="m-0 flex list-none overflow-x-auto p-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {items.map((item) => {
              const active = item.anchor === current;

              return (
                <li
                  key={item.anchor}
                  className="m-0 shrink-0"
                  data-target={item.anchor}
                >
                  <a
                    className={clsx(
                      "flex flex-col items-start gap-1 border-b-2 px-5 pt-4.5 pb-4 no-underline mobile:px-3.5 mobile:pt-3.5 mobile:pb-3",
                      "transition-[border-color] duration-400 ease-[ease]",
                      active
                        ? "border-transparent border-b-das-green"
                        : "border-transparent hover:border-b-das-green/40",
                    )}
                    href={`#${item.anchor}`}
                    onClick={(event) => jump(event, item.anchor)}
                  >
                    {item.number && (
                      <span
                        className={clsx(
                          "text-[10px] leading-[1.2] font-bold tracking-[0.18em] transition-[color] duration-400 ease-[ease]",
                          active ? "text-white" : "text-das-green",
                        )}
                      >
                        {item.number}
                      </span>
                    )}

                    {item.label && (
                      <span
                        className={clsx(
                          "text-[12px] leading-[1.2] font-medium whitespace-nowrap transition-[color] duration-400 ease-[ease]",
                          active ? "text-white/92" : "text-white/52",
                        )}
                      >
                        {item.label}
                      </span>
                    )}
                  </a>
                </li>
              );
            })}
          </ul>

          {showRail && (
            <div
              // There is no room for the rail beside the content until the viewport is wide.
              className="fixed top-1/2 left-6 z-5 flex -translate-y-1/2 flex-col gap-2.5 small-desktop:hidden"
              aria-hidden="true"
            >
              {items.map((item) => {
                const active = item.anchor === current;

                return (
                  <span
                    key={item.anchor}
                    className={clsx(
                      "flex items-center gap-2 py-[3px]",
                      enhanced && "cursor-pointer",
                    )}
                    data-target={item.anchor}
                    data-dark={item.dark ? "1" : "0"}
                    title={item.label}
                    onClick={onJump ? () => onJump(item.anchor) : undefined}
                  >
                    {/* The whole rail inverts over a dark section; the active mark is green either way. */}
                    <span
                      className={clsx(
                        "h-0.5 rounded-[1px] transition-[width,background-color] duration-400 ease-[ease] motion-reduce:transition-none",
                        active
                          ? "w-4.5 bg-das-green"
                          : clsx(
                              "w-2",
                              railOverDark ? "bg-white/28" : "bg-brand-blue/20",
                            ),
                      )}
                    ></span>

                    {item.number && (
                      <span
                        className={clsx(
                          "text-[11px] leading-none font-bold tracking-[0.1em] whitespace-nowrap",
                          "transition-[opacity,translate,color] duration-400 ease-[ease] motion-reduce:transition-none",
                          active
                            ? "translate-x-0 opacity-100"
                            : "-translate-x-1 opacity-0",
                          railOverDark ? "text-white" : "text-brand-blue",
                        )}
                      >
                        {item.number}
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
